'use client';

import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type KeyboardEvent, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';

type Preview = { src: string; label: string };

export default function PostRichContent({ html }: { html: string }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const previewScrollRef = useRef<HTMLDivElement>(null);
  const previewImageRef = useRef<HTMLImageElement>(null);
  const zoomFocusRef = useRef<{ x: number; y: number; screenX: number; screenY: number } | null>(null);
  const dragRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [viewport, setViewport] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);

  const changeZoom = (update: (value: number) => number) => {
    const scroll = previewScrollRef.current;
    const image = previewImageRef.current;
    if (scroll && image) {
      const viewportRect = scroll.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const screenX = viewportRect.left + scroll.clientWidth / 2;
      const screenY = viewportRect.top + scroll.clientHeight / 2;
      const fraction = (point: number, start: number, size: number) =>
        point >= start && point <= start + size && size > 0 ? (point - start) / size : 0.5;
      zoomFocusRef.current = {
        x: fraction(screenX, imageRect.left, imageRect.width),
        y: fraction(screenY, imageRect.top, imageRect.height),
        screenX,
        screenY,
      };
    }
    setZoom(update);
  };

  useLayoutEffect(() => {
    const focus = zoomFocusRef.current;
    const scroll = previewScrollRef.current;
    const image = previewImageRef.current;
    if (!focus || !scroll || !image) return;
    zoomFocusRef.current = null;
    const rect = image.getBoundingClientRect();
    scroll.scrollLeft += rect.left + focus.x * rect.width - focus.screenX;
    scroll.scrollTop += rect.top + focus.y * rect.height - focus.screenY;
  }, [zoom]);

  useEffect(() => {
    contentRef.current?.querySelectorAll('img').forEach(image => {
      image.tabIndex = 0;
      image.setAttribute('role', 'button');
      image.setAttribute('aria-label', '放大查看图片');
    });
  }, [html]);

  useEffect(() => {
    if (!preview) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const updateViewport = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    updateViewport();
    const onZoomKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === '+' || event.key === '=') changeZoom(value => Math.min(6, Math.round((value + 0.25) * 100) / 100));
      if (event.key === '-') changeZoom(value => Math.max(0.25, Math.round((value - 0.25) * 100) / 100));
    };
    window.addEventListener('keydown', onZoomKeyDown);
    window.addEventListener('resize', updateViewport);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onZoomKeyDown);
      window.removeEventListener('resize', updateViewport);
    };
  }, [preview]);

  const openImage = (image: HTMLImageElement) => {
    zoomFocusRef.current = null;
    dragRef.current = null;
    setDragging(false);
    setNaturalSize(null);
    setZoom(1);
    setPreview({
      src: image.currentSrc || image.src,
      label: image.closest('figure')?.querySelector('figcaption')?.textContent?.trim() || image.alt || '文章图片',
    });
  };
  const onContentClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof HTMLImageElement)) return;
    event.preventDefault();
    openImage(event.target);
  };
  const onContentKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!(event.target instanceof HTMLImageElement) || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    openImage(event.target);
  };
  const fitScale = naturalSize && viewport
    ? Math.min(1, (viewport.width - 48) / naturalSize.width, (viewport.height - 100) / naturalSize.height)
    : 1;
  const imageWidth = naturalSize ? naturalSize.width * fitScale * zoom : undefined;

  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || event.target !== previewImageRef.current) return;
    event.preventDefault();
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };
  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.x;
    const deltaY = event.clientY - drag.y;
    event.currentTarget.scrollLeft -= deltaX;
    event.currentTarget.scrollTop -= deltaY;
    drag.x = event.clientX;
    drag.y = event.clientY;
  };
  const stopDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
  };

  return <>
    <div ref={contentRef} className="guide-rich-content" dangerouslySetInnerHTML={{ __html: html }} onClick={onContentClick} onKeyDown={onContentKeyDown} />
    {preview && createPortal(
      <div className="guide-image-preview" role="dialog" aria-modal="true" aria-label="图片预览">
        <div className="guide-image-preview-actions">
          <div className="guide-image-preview-zoom">
          <button type="button" aria-label="缩小图片" disabled={zoom <= 0.25} onClick={() => changeZoom(value => Math.max(0.25, Math.round((value - 0.25) * 100) / 100))}>−</button>
          <span aria-live="polite">{Math.round(zoom * 100)}%</span>
          <button type="button" aria-label="放大图片" disabled={zoom >= 6} onClick={() => changeZoom(value => Math.min(6, Math.round((value + 0.25) * 100) / 100))}>＋</button>
          </div>
          <button type="button" onClick={() => changeZoom(() => 1)}>适应屏幕</button>
          <button type="button" aria-label="关闭图片预览" onClick={() => setPreview(null)}>×</button>
        </div>
        <div ref={previewScrollRef} className={`guide-image-preview-scroll${dragging ? ' is-dragging' : ''}`} onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={stopDrag} onPointerCancel={stopDrag}>
          <div className="guide-image-preview-stage">
          {/* The source is already an article image URL and must retain its original dimensions. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img ref={previewImageRef} src={preview.src} alt={preview.label} draggable={false} style={imageWidth ? { width: imageWidth, maxWidth: 'none', maxHeight: 'none' } : undefined} onLoad={event => setNaturalSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} />
          </div>
        </div>
      </div>, document.body,
    )}
  </>;
}
